<script setup lang="ts">
import { user } from '@ideaflow/ui/classes/user'
import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui'
import { computed, onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import type { UserInfo } from '@/stores/user'
import {
  getLegacyPlayerBridgeOrigin,
  isLegacyPlayerBridgeAuthMessage,
  LEGACY_PLAYER_BRIDGE_AUTH_MESSAGE_TYPE,
  LEGACY_PLAYER_BRIDGE_AUTH_REQUEST,
  LEGACY_PLAYER_BRIDGE_AUTH_RESPONSE,
} from '@/utils/legacy-player'

defineOptions({
  name: 'LegacyPlayerOverlay',
})

const props = defineProps<{
  open: boolean
  url: string | null
  loadKey: number
}>()

const emit = defineEmits<{
  close: []
}>()

const isLoading = shallowRef(false)
const showLoadHint = shallowRef(false)
const frameRef = useTemplateRef<HTMLIFrameElement>('frame')
let loadTimer: number | null = null
let autoplaySoundTimer: number | null = null
let autoplaySoundAttemptCount = 0
let autoplaySoundToggleClicked = false
const LEGACY_PLAYER_CLOSE_MESSAGE_FNS = new Set([
  'closePreview',
  'closeFrame',
  'closeFrameviewer',
  'close',
])
const LEGACY_PLAYER_AUTOPLAY_SOUND_RETRY_MS = 300
const LEGACY_PLAYER_AUTOPLAY_SOUND_MAX_ATTEMPTS = 50

const frameTitle = computed(() => {
  if (!props.url) {
    return '内容播放器'
  }

  try {
    const parsed = new URL(props.url)

    if (parsed.pathname === '/radio.html') {
      return '听听播放器'
    }

    if (parsed.pathname === '/rolematerial.html') {
      return '内容播放器'
    }

    return '互动内容播放器'
  } catch {
    return '内容播放器'
  }
})

const bridgeFrameOrigin = computed(() => {
  if (!props.url) {
    return null
  }

  try {
    const frameUrl = new URL(props.url)
    const bridgeOrigin = getLegacyPlayerBridgeOrigin()

    return frameUrl.origin === bridgeOrigin ? frameUrl.origin : null
  } catch {
    return null
  }
})

function getCurrentUserInfo(): UserInfo | null {
  const cookieUser = user.get() as unknown as UserInfo | null

  if (!cookieUser?.id && !cookieUser?.uid) {
    return null
  }

  return JSON.parse(JSON.stringify(cookieUser)) as UserInfo
}

function clearLoadTimer() {
  if (loadTimer === null) {
    return
  }

  window.clearTimeout(loadTimer)
  loadTimer = null
}

function clearAutoplaySoundTimer() {
  if (autoplaySoundTimer === null) {
    return
  }

  window.clearTimeout(autoplaySoundTimer)
  autoplaySoundTimer = null
}

function resetAutoplaySoundSync() {
  clearAutoplaySoundTimer()
  autoplaySoundAttemptCount = 0
  autoplaySoundToggleClicked = false
}

function isLegacyPlayerAutoplaySoundUrl(url: string | null): boolean {
  if (!url) {
    return false
  }

  try {
    const parsed = new URL(url)

    return parsed.pathname === '/rolematerial.html' && parsed.searchParams.get('play') === '1'
  } catch {
    return false
  }
}

function getSameOriginFrameDocument(): Document | null {
  if (
    typeof window === 'undefined' ||
    !isLegacyPlayerAutoplaySoundUrl(props.url) ||
    !frameRef.value
  ) {
    return null
  }

  try {
    const frameUrl = new URL(props.url ?? '')

    if (frameUrl.origin !== window.location.origin) {
      return null
    }

    return frameRef.value.contentDocument
  } catch {
    return null
  }
}

function getLegacyPlayerSoundToggle(frameDocument: Document): HTMLElement | null {
  return frameDocument.querySelector<HTMLElement>('.video-tools-item[data-tool-id="soundSwitch"]')
}

function hasLegacyPlayerSoundOffIcon(soundToggle: HTMLElement): boolean {
  return Boolean(soundToggle.querySelector('[class*="icon-sound-off"]'))
}

function markLegacyPlayerSoundToggleOn(soundToggle: HTMLElement) {
  const soundOffIcon = soundToggle.querySelector<HTMLElement>('[class*="icon-sound-off"]')

  if (!soundOffIcon) {
    return
  }

  soundOffIcon.classList.remove('icon-sound-off-fill', 'icon-sound-off_thin')
  soundOffIcon.classList.add('icon-sound-on_thin')
}

function syncLegacyPlayerVideoSound(frameDocument: Document): boolean {
  const videos = Array.from(frameDocument.querySelectorAll('video'))

  videos.forEach((video) => {
    video.muted = false
    video.volume = 1

    if (video.paused) {
      void video.play().catch(() => undefined)
    }
  })

  return videos.length > 0 && videos.every((video) => !video.muted)
}

function syncLegacyPlayerSoundToggle(frameDocument: Document): boolean {
  const soundToggle = getLegacyPlayerSoundToggle(frameDocument)

  if (!soundToggle) {
    return false
  }

  if (!autoplaySoundToggleClicked && hasLegacyPlayerSoundOffIcon(soundToggle)) {
    autoplaySoundToggleClicked = true
    soundToggle.click()
  }

  if (autoplaySoundToggleClicked && hasLegacyPlayerSoundOffIcon(soundToggle)) {
    markLegacyPlayerSoundToggleOn(soundToggle)
  }

  return !hasLegacyPlayerSoundOffIcon(soundToggle)
}

function syncLegacyPlayerAutoplaySound() {
  const frameDocument = getSameOriginFrameDocument()

  if (!frameDocument) {
    return false
  }

  const isToggleSynced = syncLegacyPlayerSoundToggle(frameDocument)
  const isVideoSynced = syncLegacyPlayerVideoSound(frameDocument)

  return isToggleSynced && isVideoSynced
}

function startAutoplaySoundSync() {
  clearAutoplaySoundTimer()

  if (!isLegacyPlayerAutoplaySoundUrl(props.url)) {
    return
  }

  const isSynced = syncLegacyPlayerAutoplaySound()

  autoplaySoundAttemptCount += 1

  if (isSynced || autoplaySoundAttemptCount >= LEGACY_PLAYER_AUTOPLAY_SOUND_MAX_ATTEMPTS) {
    return
  }

  autoplaySoundTimer = window.setTimeout(
    startAutoplaySoundSync,
    LEGACY_PLAYER_AUTOPLAY_SOUND_RETRY_MS,
  )
}

function startLoading() {
  clearLoadTimer()
  showLoadHint.value = false
  isLoading.value = Boolean(props.open && props.url)

  if (!isLoading.value) {
    return
  }

  loadTimer = window.setTimeout(() => {
    showLoadHint.value = true
  }, 9000)
}

function handleLoaded() {
  clearLoadTimer()
  isLoading.value = false
  showLoadHint.value = false
  startAutoplaySoundSync()
}

function handleOpenChange(nextOpen: boolean) {
  if (!nextOpen) {
    emit('close')
  }
}

function getLegacyPlayerMessageFn(data: unknown) {
  if (!data || typeof data !== 'object') {
    return null
  }

  const fn = (data as { data?: { fn?: unknown } }).data?.fn

  return typeof fn === 'string' ? fn : null
}

function handleLegacyPlayerCloseMessage(event: MessageEvent) {
  const targetWindow = frameRef.value?.contentWindow

  if (
    !targetWindow ||
    event.source !== targetWindow ||
    !LEGACY_PLAYER_CLOSE_MESSAGE_FNS.has(getLegacyPlayerMessageFn(event.data) ?? '')
  ) {
    return
  }

  emit('close')
}

function handleBridgeAuthMessage(event: MessageEvent) {
  const targetWindow = frameRef.value?.contentWindow
  const targetOrigin = bridgeFrameOrigin.value

  if (
    !targetWindow ||
    !targetOrigin ||
    event.source !== targetWindow ||
    event.origin !== targetOrigin ||
    !isLegacyPlayerBridgeAuthMessage(event.data) ||
    event.data.action !== LEGACY_PLAYER_BRIDGE_AUTH_REQUEST
  ) {
    return
  }

  targetWindow.postMessage(
    {
      type: LEGACY_PLAYER_BRIDGE_AUTH_MESSAGE_TYPE,
      id: event.data.id,
      action: LEGACY_PLAYER_BRIDGE_AUTH_RESPONSE,
      data: getCurrentUserInfo(),
    },
    targetOrigin,
  )
}

function handleMessage(event: MessageEvent) {
  handleLegacyPlayerCloseMessage(event)
  handleBridgeAuthMessage(event)
}

onBeforeUnmount(() => {
  clearLoadTimer()
  clearAutoplaySoundTimer()
  window.removeEventListener('message', handleMessage)
})

onMounted(() => {
  window.addEventListener('message', handleMessage)
})

watch(
  () => [props.open, props.url, props.loadKey] as const,
  () => {
    startLoading()
    resetAutoplaySoundSync()
  },
  { immediate: true },
)
</script>

<template>
  <DialogRoot :open="open" @update:open="handleOpenChange">
    <DialogPortal>
      <DialogOverlay class="legacy-player__overlay" />
      <DialogContent
        class="legacy-player"
        :aria-describedby="undefined"
        @escape-key-down="emit('close')"
      >
        <DialogTitle class="legacy-player__sr-title">{{ frameTitle }}</DialogTitle>

        <div class="legacy-player__panel">
          <div class="legacy-player__body">
            <div v-if="isLoading" class="legacy-player__loading" aria-live="polite">
              <span class="legacy-player__spinner" aria-hidden="true" />
              <span v-if="showLoadHint">播放器加载较慢，请稍后</span>
            </div>

            <iframe
              v-if="url"
              :key="loadKey"
              ref="frame"
              class="legacy-player__frame"
              :src="url"
              :title="frameTitle"
              allow="autoplay; clipboard-write; fullscreen; picture-in-picture"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
              referrerpolicy="strict-origin-when-cross-origin"
              @load="handleLoaded"
            />
          </div>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style scoped>
.legacy-player__overlay {
  position: fixed;
  z-index: 100;
  background: rgba(0, 0, 0, 0.5);
  inset: 0;
  animation: legacy-player-overlay-in 120ms ease-out;
}

.legacy-player {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 101;
  display: flex;
  width: min(960px, calc(100vw - 48px));
  height: min(860px, calc(100dvh - 48px));
  min-height: 520px;
  overflow: visible;
  background: transparent;
  color: #fff;
  outline: none;
  transform: translate(-50%, -50%);
  animation: legacy-player-panel-in 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.legacy-player__sr-title {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.legacy-player__panel {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  background: #000;
  box-shadow: 0 28px 86px rgba(0, 0, 0, 0.34);
}

.legacy-player__body {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
}

.legacy-player__frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: #000;
}

.legacy-player__loading {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: radial-gradient(circle at 50% 40%, rgba(255, 255, 255, 0.08), transparent 34%), #050506;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
}

.legacy-player__spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: rgba(255, 255, 255, 0.82);
  border-radius: 999px;
  animation: legacy-player-spin 0.8s linear infinite;
}

@keyframes legacy-player-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes legacy-player-overlay-in {
  from {
    opacity: 0;
  }
}

@keyframes legacy-player-panel-in {
  from {
    opacity: 0;
    transform: translate(-50%, -49%) scale(0.985);
  }
}

@media (max-width: 1080px) and (min-width: 961px) {
  .legacy-player {
    width: min(960px, calc(100vw - 112px));
  }
}

@media (max-width: 960px) {
  .legacy-player {
    top: 0;
    left: 0;
    width: 100vw;
    height: 100dvh;
    min-height: 0;
    transform: none;
    animation-name: legacy-player-panel-in-mobile;
  }

  .legacy-player__panel {
    border-radius: 0;
  }

  @keyframes legacy-player-panel-in-mobile {
    from {
      opacity: 0;
      transform: scale(0.995);
    }
  }
}
</style>
